import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { ContactRole, ContactDetailType } from '@/api/models/contact';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { Contact, QUERY_KEY as GET_ALL_QUERY_KEY } from './useGetCommunityContacts';

export interface UpdateContactDetailDto {
  id?: number;
  type: ContactDetailType;
  value: string;
  label?: string;
  is_primary?: boolean;
}

export interface ApiRequest_Update_CommunityContact {
  name: string;
  user_id?: string;
  role: ContactRole;
  metadata?: Record<string, unknown>;
  details?: UpdateContactDetailDto[];
}

export type ApiResponse_Update_CommunityContact = Contact;

const ENDPOINT = 'contacts/community';
const METHOD = 'PATCH';

export const useUpdateCommunityContact = (
  contactId: number,
  communityId: number,
  stateFns?: MutationStateFns<ApiResponse_Update_CommunityContact>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: ApiRequest_Update_CommunityContact
    ): Promise<ApiResponse_Update_CommunityContact> => {
      const updatedContact = await handleApiCall<
        ApiResponse_Update_CommunityContact,
        ApiRequest_Update_CommunityContact
      >(
        session,
        METHOD,
        `${ENDPOINT}/${contactId}`,
        data,
        {
          params: {
            community_id: communityId,
          },
        }
      );

      return updatedContact;
    },
    onSuccess: data => {
      enqueueSnackbar(`Contact "${data.name}" updated successfully`, { variant: 'success' });

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: GET_ALL_QUERY_KEY(communityId),
      });
      queryClient.invalidateQueries({
        queryKey: ['contacts', 'community', 'single', contactId, communityId],
      });

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar('Failed to update contact', { variant: 'error' });
      stateFns?.onError?.(err);
    },
  });
};